const MAX_IMAGE_EDGE = 1200;
const IMAGE_QUALITY = 0.8;

export const optimizeProductImage = async (file: File): Promise<File> => {
    if (!file.type.startsWith('image/')) throw new Error('이미지 파일을 선택해 주세요.');
    // Preserve animations and vectors instead of flattening them into a single raster frame.
    if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

    const url = URL.createObjectURL(file);
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('이미지를 읽지 못했습니다. 다른 사진을 선택해 주세요.'));
            image.src = url;
        });
        if (!image.naturalWidth || !image.naturalHeight) throw new Error('이미지 크기를 확인하지 못했습니다.');

        const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('이 브라우저에서는 이미지를 처리할 수 없습니다.');
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(result => result ? resolve(result) : reject(new Error('이미지 압축에 실패했습니다.')), 'image/webp', IMAGE_QUALITY);
        });
        // Do not inflate already-small files. Browsers without WebP encoding return PNG.
        if (blob.size >= file.size) return file;
        const extension = blob.type === 'image/webp' ? 'webp' : 'png';
        const name = file.name.replace(/\.[^.]+$/, '') || 'menu';
        return new File([blob], `${name}.${extension}`, { type: blob.type, lastModified: file.lastModified });
    } finally {
        URL.revokeObjectURL(url);
    }
};
